import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { format, parseISO, startOfMonth } from 'date-fns';
import CustomMonthCalendar from '../../common/CustomMonthCalendar';
import { getAvailableSlots, getAvailableDays } from '../../../services/expert.service';
import { appointmentService } from '../../../services/appointment.service';
import { toastService } from '../../../services/toast.service';
import { AppointmentParticipantOption } from '../../../types/appointment.types';
import { AvailableSlot } from '../../../types/expert';
import { ModalIndices } from '../../../common/i-doc/i-doc.registry';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  expertId: number;
  clients: AppointmentParticipantOption[];
  preselectedClient?: AppointmentParticipantOption;
}

export const CreateAppointmentByExpertDialog: React.FC<Props> = ({
  open, onClose, onSuccess, expertId, clients, preselectedClient,
}) => {
  const [selectedClient, setSelectedClient] = useState<AppointmentParticipantOption | null>(null);
  const [currentMonth, setCurrentMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [availableDays, setAvailableDays] = useState<string[]>([]);
  const [daysLoading, setDaysLoading] = useState(false);
  const [slots, setSlots] = useState<AvailableSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [expertNotes, setExpertNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const fetchDays = useCallback(async (month: string) => {
    setDaysLoading(true);
    try {
      const days = await getAvailableDays(expertId, month);
      setAvailableDays(days);
    } catch {
      setAvailableDays([]);
    } finally {
      setDaysLoading(false);
    }
  }, [expertId]);

  const fetchSlots = useCallback(async (date: Date | null, clientId?: number) => {
    if (!date) return;
    setSlotsLoading(true);
    setSlotsError(null);
    setSelectedSlot(null);
    setSlots([]);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const result = await getAvailableSlots(expertId, dateStr, clientId);
      setSlots(result);
    } catch {
      setSlotsError('Не вдалося завантажити доступні слоти.');
    } finally {
      setSlotsLoading(false);
    }
  }, [expertId]);

  useEffect(() => {
    if (open) {
      const today = new Date();
      const client = preselectedClient ?? null;
      setSelectedClient(client);
      setCurrentMonth(format(today, 'yyyy-MM'));
      setSelectedDate(today);
      setAvailableDays([]);
      setSlots([]);
      setSelectedSlot(null);
      setExpertNotes('');
      setSubmitError(null);
      fetchDays(format(startOfMonth(today), 'yyyy-MM'));
      fetchSlots(today, client?.id);
    }
  }, [open, preselectedClient, fetchDays, fetchSlots]);

  useEffect(() => {
    if (!open || !selectedDate) return;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    if (availableDays.includes(dateStr)) {
      fetchSlots(selectedDate, selectedClient?.id);
    } else {
      setSlots([]);
      setSelectedSlot(null);
    }
  }, [open, selectedDate, availableDays, fetchSlots, selectedClient?.id]);

  const handleSubmit = async () => {
    if (!selectedClient || !selectedSlot) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await appointmentService.createAppointmentByExpert({
        clientId: selectedClient.id,
        scheduledAt: selectedSlot,
        expertNotes: expertNotes || undefined,
      });
      toastService.success('Зустріч створено.');
      onSuccess();
      onClose();
    } catch {
      setSubmitError('Не вдалося створити зустріч. Можливо, слот вже зайнятий.');
    } finally {
      setSubmitting(false);
    }
  };

  const step = preselectedClient ? 1 : 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" data-i-doc={ModalIndices.EXPERT_CREATE_APPOINTMENT}>
      <DialogTitle>Нова зустріч{preselectedClient ? ` — ${preselectedClient.name}` : ''}</DialogTitle>
      <DialogContent dividers>
        {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
        {!preselectedClient && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>1. Виберіть клієнта</Typography>
            <Autocomplete
              options={clients}
              getOptionLabel={(o) => o.name}
              value={selectedClient}
              onChange={(_, v) => setSelectedClient(v)}
              renderInput={(params) => (
                <TextField {...params} label="Клієнт" placeholder="Почніть вводити ім'я..." />
              )}
              noOptionsText="Клієнтів не знайдено"
            />
          </Box>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>{step + 1}. Виберіть дату</Typography>
            <CustomMonthCalendar
              currentMonth={currentMonth}
              onMonthChange={(m) => { setCurrentMonth(m); fetchDays(m); }}
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              highlightedDates={availableDays}
              disabledDates={[]}
            />
            {daysLoading && <CircularProgress size={24} sx={{ mt: 2 }} />}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>{step + 2}. Виберіть час</Typography>
            {slotsLoading ? (
              <CircularProgress />
            ) : slotsError ? (
              <Alert severity="error">{slotsError}</Alert>
            ) : slots.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {slots.map((slot, i) => (
                  <Chip
                    key={`${slot.startTime}-${i}`}
                    label={format(parseISO(slot.startTime), 'HH:mm')}
                    onClick={() => slot.isAvailable && setSelectedSlot(slot.startTime)}
                    disabled={!slot.isAvailable}
                    color={selectedSlot === slot.startTime ? 'primary' : 'default'}
                    variant={selectedSlot === slot.startTime ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary">На вибрану дату немає вільних слотів.</Typography>
            )}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>{step + 3}. Нотатки (необов'язково)</Typography>
              <TextField
                label="Нотатки для клієнта"
                value={expertNotes}
                onChange={(e) => setExpertNotes(e.target.value)}
                multiline
                rows={3}
                fullWidth
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={onClose} disabled={submitting}>Скасувати</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selectedClient || !selectedSlot || submitting}
        >
          {submitting ? <CircularProgress size={20} /> : 'Створити зустріч'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
